import { IonSpinner } from '@ionic/react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';

import { lookupCep, lookupCnpj, streetWithType } from '~/api/clients';
import { FieldWrap, MaskedInput } from '~/components/forms/FormField';
import { Icon } from '~/components/ui/Icon';
import { SectionHead } from '~/components/ui/SectionHead';
import {
  applyCepMask,
  applyCpfCnpjMask,
  digitsOnly,
  isValidCepLength,
  isValidCpfCnpjLength,
} from '~/lib/masks';
import { useWizard } from '~/pages/emitir/wizardContext';
import { type TomadorInput, tomadorSchema } from '~/schemas/invoice';

interface StepTomadorProps {
  fieldErrors: Record<string, string>;
}

export function StepTomador({ fieldErrors }: StepTomadorProps): JSX.Element {
  const { draft, setTomador } = useWizard();
  const { tomador } = draft;
  const [cnpjError, setCnpjError] = useState<string | null>(null);
  const [cepError, setCepError] = useState<string | null>(null);

  const isCnpj = digitsOnly(tomador.cpf_cnpj).length === 14;
  const cpfCnpjValid = isValidCpfCnpjLength(tomador.cpf_cnpj);

  const cnpjMutation = useMutation({
    mutationFn: (cnpj: string) => lookupCnpj(cnpj),
    onSuccess: (data) => {
      setCnpjError(null);
      setTomador((prev) => ({
        ...prev,
        razao_social: data.razao_social || prev.razao_social,
        email: data.email || prev.email,
        cep: digitsOnly(data.cep) || prev.cep,
        logradouro: streetWithType(data) || prev.logradouro,
        numero: data.numero || prev.numero,
        complemento: data.complemento || prev.complemento,
        bairro: data.bairro || prev.bairro,
        codigo_cidade: data.codigo_municipio || prev.codigo_cidade,
        descricao_cidade: data.municipio || prev.descricao_cidade,
        estado: data.uf || prev.estado,
      }));
    },
    onError: (error) => {
      const message = extractAxiosMessage(error, 'CNPJ não encontrado na base.');
      setCnpjError(message);
    },
  });

  const cepMutation = useMutation({
    mutationFn: (cep: string) => lookupCep(cep),
    onSuccess: (data) => {
      setCepError(null);
      setTomador((prev) => ({
        ...prev,
        logradouro: data.logradouro || prev.logradouro,
        bairro: data.bairro || prev.bairro,
        descricao_cidade: data.municipio || prev.descricao_cidade,
        estado: data.uf || prev.estado,
        codigo_cidade: data.ibge || prev.codigo_cidade,
        complemento: data.complemento || prev.complemento,
      }));
    },
    onError: () => {
      setCepError('CEP não encontrado.');
    },
  });

  const handleCpfCnpjChange = (raw: string): void => {
    setCnpjError(null);
    setTomador({ ...tomador, cpf_cnpj: applyCpfCnpjMask(raw) });
  };

  const handleCpfCnpjBlur = (): void => {
    if (!isCnpj || !cpfCnpjValid) return;
    cnpjMutation.mutate(digitsOnly(tomador.cpf_cnpj));
  };

  const handleCepChange = (raw: string): void => {
    setCepError(null);
    const masked = applyCepMask(raw);
    setTomador({ ...tomador, cep: masked });
    if (isValidCepLength(masked)) {
      cepMutation.mutate(digitsOnly(masked));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionHead label="Documento" />
      <div style={{ padding: '0 0' }}>
        <div
          style={{
            background: 'var(--rx-surface)',
            border: '0.5px solid var(--rx-border)',
            borderRadius: 14,
            padding: '16px 16px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <FieldWrap
            id="tomador-doc"
            label={isCnpj ? 'CNPJ do tomador' : 'CPF/CNPJ do tomador'}
            error={cnpjError ?? fieldErrors.cpf_cnpj}
            hint={
              isCnpj
                ? 'Preencho os outros campos automaticamente.'
                : 'Para CPF, preencha os dados manualmente.'
            }
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <MaskedInput
                  id="tomador-doc"
                  value={tomador.cpf_cnpj}
                  onChange={handleCpfCnpjChange}
                  inputmode="numeric"
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
              </div>
              {isCnpj && (
                <button
                  type="button"
                  onClick={handleCpfCnpjBlur}
                  disabled={!cpfCnpjValid || cnpjMutation.isPending}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '6px 10px',
                    borderRadius: 999,
                    background: 'var(--rx-brand-tint)',
                    color: 'var(--rx-brand)',
                    border: 'none',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                    textTransform: 'uppercase',
                    cursor: cpfCnpjValid && !cnpjMutation.isPending ? 'pointer' : 'not-allowed',
                    opacity: cpfCnpjValid && !cnpjMutation.isPending ? 1 : 0.4,
                  }}
                >
                  {cnpjMutation.isPending ? (
                    <IonSpinner name="dots" style={{ width: 14, height: 14 }} />
                  ) : (
                    <>
                      <Icon name="search" size={12} />
                      Buscar
                    </>
                  )}
                </button>
              )}
            </div>
          </FieldWrap>
        </div>
      </div>

      <SectionHead label="Identificação" />
      <div
        style={{
          background: 'var(--rx-surface)',
          border: '0.5px solid var(--rx-border)',
          borderRadius: 14,
          padding: '16px 16px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <FieldWrap id="tomador-razao" label="Razão social / Nome" error={fieldErrors.razao_social}>
          <MaskedInput
            id="tomador-razao"
            value={tomador.razao_social}
            onChange={(value) => setTomador({ ...tomador, razao_social: value })}
            placeholder="Nome ou empresa do tomador"
            maxLength={255}
          />
        </FieldWrap>

        <FieldWrap
          id="tomador-email"
          label="E-mail"
          optional
          error={fieldErrors.email}
          hint="Receberá o XML/PDF se preenchido."
        >
          <MaskedInput
            id="tomador-email"
            type="email"
            value={tomador.email ?? ''}
            onChange={(value) => setTomador({ ...tomador, email: value })}
            inputmode="email"
            autocomplete="email"
            placeholder="cliente@exemplo.com"
          />
        </FieldWrap>

        <FieldWrap id="tomador-im" label="Inscrição municipal" optional>
          <MaskedInput
            id="tomador-im"
            value={tomador.inscricao_municipal ?? ''}
            onChange={(value) => setTomador({ ...tomador, inscricao_municipal: value })}
            maxLength={20}
          />
        </FieldWrap>
      </div>

      <SectionHead
        label="Endereço"
        action={cepMutation.isPending ? <span className="rx-mono">buscando…</span> : null}
      />
      <div
        style={{
          background: 'var(--rx-surface)',
          border: '0.5px solid var(--rx-border)',
          borderRadius: 14,
          padding: '16px 16px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <FieldWrap
          id="tomador-cep"
          label="CEP"
          optional
          error={cepError ?? fieldErrors.cep}
          hint="Preencho o endereço automaticamente."
        >
          <MaskedInput
            id="tomador-cep"
            value={tomador.cep ?? ''}
            onChange={handleCepChange}
            inputmode="numeric"
            placeholder="00000-000"
            maxLength={9}
          />
        </FieldWrap>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <FieldWrap id="tomador-logradouro" label="Logradouro" optional>
            <MaskedInput
              id="tomador-logradouro"
              value={tomador.logradouro ?? ''}
              onChange={(value) => setTomador({ ...tomador, logradouro: value })}
              placeholder="Rua / Av."
            />
          </FieldWrap>
          <FieldWrap id="tomador-numero" label="Número" optional>
            <MaskedInput
              id="tomador-numero"
              value={tomador.numero ?? ''}
              onChange={(value) => setTomador({ ...tomador, numero: value })}
              inputmode="numeric"
            />
          </FieldWrap>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FieldWrap id="tomador-complemento" label="Complemento" optional>
            <MaskedInput
              id="tomador-complemento"
              value={tomador.complemento ?? ''}
              onChange={(value) => setTomador({ ...tomador, complemento: value })}
            />
          </FieldWrap>
          <FieldWrap id="tomador-bairro" label="Bairro" optional>
            <MaskedInput
              id="tomador-bairro"
              value={tomador.bairro ?? ''}
              onChange={(value) => setTomador({ ...tomador, bairro: value })}
            />
          </FieldWrap>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <FieldWrap id="tomador-cidade" label="Cidade" optional>
            <MaskedInput
              id="tomador-cidade"
              value={tomador.descricao_cidade ?? ''}
              onChange={(value) => setTomador({ ...tomador, descricao_cidade: value })}
            />
          </FieldWrap>
          <FieldWrap id="tomador-uf" label="UF" optional error={fieldErrors.estado}>
            <MaskedInput
              id="tomador-uf"
              value={tomador.estado ?? ''}
              onChange={(value) =>
                setTomador({ ...tomador, estado: value.toUpperCase().slice(0, 2) })
              }
              maxLength={2}
              placeholder="SP"
            />
          </FieldWrap>
        </div>
      </div>
    </div>
  );
}

function extractAxiosMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { error?: string; detail?: string } | undefined;
    return data?.error ?? data?.detail ?? fallback;
  }
  return fallback;
}

export function validateTomador(value: TomadorInput): Record<string, string> {
  const result = tomadorSchema.safeParse(value);
  if (result.success) return {};
  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string' && !errors[key]) {
      errors[key] = issue.message;
    }
  }
  return errors;
}
